import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useState, useLayoutEffect } from 'react';
import { useSharedRef } from '../hooks/use-shared-ref.js';
import { observeResizeAll } from '../utils/observe-resize.js';
import { useOnTruncationChange } from './text.hooks.js';
import { onFontsReady, ZERO_WIDTH_SPACE, baseClassName } from './text.utils.js';
import { truncateMiddleLeft, truncateMiddleRight, truncateMiddleRightContent, truncateMiddleContainer } from './text.css.js';

/**
 * Renders middle-elided text (e.g. `"hello world"` → `"hel... rld"`) using a two-span layout
 * that masks the right half. Requires `children` to be a string so the same value can render in
 * both halves.
 */
// eslint-disable-next-line react/display-name -- internal helper, not exposed in public API
const MiddleTruncatedText = forwardRef(({ as, variant, color = 'inherit', align, italic, underline, strikethrough, hideTooltip, onTruncationChange, className, children, ...props }, refIn) => {
    const ref = useSharedRef(refIn);
    const leftRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);
    useLayoutEffect(() => {
        const check = () => {
            const left = leftRef.current;
            const container = ref.current;
            if (!left || !container)
                return;
            setIsTruncated(left.scrollWidth > Math.ceil(container.clientWidth));
        };
        check();
        const stopResize = observeResizeAll([ref, leftRef], check);
        const stopFontsReady = onFontsReady(check);
        return () => {
            stopFontsReady();
            stopResize();
        };
    }, [children, ref]);
    useOnTruncationChange(isTruncated, onTruncationChange);
    // Middle truncation always receives a string child (the factory's dispatch enforces it), so
    // we can populate the tooltip eagerly here instead of going through `useTooltip`'s lazy
    // pointer-enter path.
    const tooltipProps = isTruncated && !hideTooltip ? { 'data-tooltip': children, 'data-tooltip-type': 'text' } : null;
    const Element = as;
    return (jsxs(Element, { "data-fpl-component": "", ...props, ref: ref, className: baseClassName({ variant, color, align, italic, underline, strikethrough }, truncateMiddleContainer, className), ...tooltipProps, children: [jsx("span", { ref: leftRef, className: truncateMiddleLeft, children: children }), isTruncated ? (jsxs("span", { className: truncateMiddleRight, "aria-hidden": "true", children: [ZERO_WIDTH_SPACE, jsx("span", { className: truncateMiddleRightContent, children: children })] })) : null] }));
});

export { MiddleTruncatedText };
//# sourceMappingURL=text.middle-truncated.js.map
